import { useEffect, useRef, useState, useCallback } from "react";
import { fetchProductSellingPointResult, generateProductSellingPoints, getInferSellingPoints } from "@apis/http/api";

interface FetchSellingPointsParams {
  id: string;
  country: string;
  attributeKey?: string;
}

interface InferSellingPointsParams {
  imageUrl: string;
  productTitle: string;
  productCategory?: string;
}

interface UseSellingPointsOptions {
  pollingInterval?: number;
}

export interface SellingPointsData {
  data: any[];
  status: Status;
  error: Error | null;
}

interface UseSellingPointsResult {
  sellingPointsData: SellingPointsData;
  inferredCategory: string;
  fetchSellingPoints: (params: FetchSellingPointsParams) => void;
  inferSellingPoints: (params: InferSellingPointsParams) => void;
}

const useSellingPoints = (options?: UseSellingPointsOptions): UseSellingPointsResult => {
  const { pollingInterval = 5000 } = options || {};

  const [data, setData] = useState<any>([]);
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<Error | null>(null);
  const [inferredCategory, setInferredCategory] = useState<string>("");
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = undefined;
    }
  }, []);

  const fetchSellingPoints = useCallback(
    async ({ id, country, attributeKey = "selling_points" }: FetchSellingPointsParams) => {
      // 先清理之前的定时器
      clearTimer();

      if (!id || !country) return;

      setStatus("loading");
      setError(null);
      setData([]);

      const startSellingPointsTask = async () => {
        try {
          const code = (await generateProductSellingPoints({
            id,
            country,
          } as any)) as unknown as number;

          if (code === 2) {
            clearTimer();
            const result: any = await fetchProductSellingPointResult({
              id,
              attributeKey,
              country,
            } as any);
            setData(result.map((item: any) => ({ ...item, checked: true })));
            setStatus("succeeded");
          }
        } catch (err) {
          clearTimer();
          setError(err instanceof Error ? err : new Error("Failed to fetch selling points"));
          setStatus("failed");
        }
      };

      startSellingPointsTask();
      timerRef.current = setInterval(startSellingPointsTask, pollingInterval);
    },
    [pollingInterval, clearTimer],
  );

  const inferSellingPoints = useCallback(
    async ({ imageUrl, productTitle, productCategory }: InferSellingPointsParams) => {
      clearTimer();

      if (!imageUrl || !productTitle) return;

      setStatus("loading");
      setError(null);
      setData([]);

      try {
        const { category, sellingPoints = [] } = await getInferSellingPoints({
          imageUrl,
          title: productTitle,
          category: productCategory,
        });
        setData(Array.isArray(sellingPoints) ? sellingPoints : []);
        if (category) {
          setInferredCategory(category);
        }
        setStatus("succeeded");
      } catch (err) {
        setError(err instanceof Error ? err : new Error("Failed to infer selling points"));
        setStatus("failed");
      }
    },
    [clearTimer],
  );

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      clearTimer();
    };
  }, [clearTimer]);

  return {
    sellingPointsData: { data, status, error },
    inferredCategory,
    fetchSellingPoints,
    inferSellingPoints,
  };
};

export default useSellingPoints;
